import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import AuthService from '../utils/authService';
import '../styles/neumorphism.css';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiShield, FiUsers, FiSave, FiX, FiLoader } = FiIcons;

function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    department_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, departmentsData] = await Promise.all([
        AuthService.getAllUsers(),
        AuthService.getAllDepartments()
      ]);
      setUsers(usersData);
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await AuthService.createUser(userData);
      await loadData();
      setShowUserForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Fehler beim Erstellen des Benutzers');
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      await AuthService.updateUser(editingUser.id, userData);
      await loadData();
      setShowUserForm(false);
      setEditingUser(null);
      resetForm();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Fehler beim Aktualisieren des Benutzers');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      try {
        await AuthService.deleteUser(userId);
        await loadData();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Fehler beim Löschen des Benutzers');
      }
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      department_ids: user.departments?.map(d => d.id) || []
    });
    setShowUserForm(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'user',
      department_ids: []
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingUser) {
      handleUpdateUser(formData);
    } else {
      handleCreateUser(formData);
    }
  };

  // Nur Admins können die Benutzerverwaltung sehen - moved check after hooks
  if (!user || user.role !== 'admin') {
    return (
      <div className="p-6 text-center">
        <SafeIcon icon={FiShield} className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Zugriff verweigert</h3>
        <p className="text-gray-600">Sie haben keine Berechtigung für die Benutzerverwaltung.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Benutzerverwaltung</h1>
          <p className="mt-2 text-gray-600">Verwalten Sie Benutzerkonten und Abteilungszugriffe</p>
        </div>
        <button
          onClick={() => setShowUserForm(true)}
          className="flex items-center px-4 py-2 neu-button bg-orange-50 text-orange-700"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
          Benutzer hinzufügen
        </button>
      </div>

      <div className="neu-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Benutzer</h3>
        </div>
        
        {users.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiUser} className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Benutzer</h3>
            <p className="text-gray-500">Erstellen Sie den ersten Benutzer</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Benutzer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Rolle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Abteilungen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 neu-element rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium neu-element-inset ${
                        user.role === 'admin' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        <SafeIcon icon={user.role === 'admin' ? FiShield : FiUser} className="w-3 h-3 mr-1" />
                        {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.departments?.map((dept) => (
                          <span key={dept.id} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium neu-element-inset bg-green-50 text-green-700">
                            {dept.name}
                          </span>
                        )) || (
                          <span className="text-sm text-gray-400">Keine Abteilungen</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium neu-element-inset ${
                        user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {user.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => startEdit(user)}
                          className="text-orange-600 hover:text-orange-900 p-2 neu-button"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900 p-2 neu-button"
                          disabled={user.id === user.id} // Prevent self-deletion
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neu-card bg-white max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
              </h2>
              <button
                onClick={() => {
                  setShowUserForm(false);
                  setEditingUser(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 p-2 neu-button"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vollständiger Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="neu-input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="neu-input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passwort {editingUser ? '(leer lassen für keine Änderung)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="neu-input w-full"
                  required={!editingUser}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rolle *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="neu-input w-full"
                  required
                >
                  <option value="user">Benutzer</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Abteilungen *
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto neu-element-inset p-3">
                  {departments.map((department) => (
                    <label key={department.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.department_ids.includes(department.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              department_ids: [...prev.department_ids, department.id]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              department_ids: prev.department_ids.filter(id => id !== department.id)
                            }));
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{department.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserForm(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 neu-button text-gray-700"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 neu-button bg-orange-50 text-orange-700"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingUser ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;